<template>
  <bly-modal
    :show.sync="show"
    :overflow="false"
    :scroll-body="false"
    width="w-full sm:max-w-5xl"
    :panel-class="panelClass"
    content-class="permission-modal__content"
    body-class="mt-0 text-left"
  >
    <template #header>
      <div class="pr-8 pb-2.5">
        <div>
          <h2 class="m-0 text-[20px] text-[#e5eefc]">{{ title }}</h2>
          <p class="mt-1.5 mb-0 text-xs text-[#97a5ba]">{{ subtitle }}</p>
        </div>
      </div>
    </template>

    <div class="min-h-0">
      <slot></slot>
    </div>

    <template #footer>
      <div class="flex w-full items-center justify-between gap-3 max-sm:flex-col max-sm:items-stretch">
        <div class="min-w-0">
          <slot name="footer-start"></slot>
        </div>
        <div class="flex justify-end gap-2.5 max-sm:justify-stretch">
          <button
            type="button"
            class="min-h-[38px] min-w-[108px] rounded-sm border border-transparent bg-[#1b242d] px-3.5 text-xs font-semibold text-[#d7dfec] transition-colors duration-150 hover:bg-[#222d39] hover:text-white max-sm:flex-1"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            class="min-h-[38px] min-w-[108px] rounded-sm border border-transparent bg-[#ffaa00] px-3.5 text-xs font-semibold text-black! transition-opacity duration-150 disabled:cursor-wait disabled:opacity-75 max-sm:flex-1"
            :disabled="loading"
            @click="$emit('save')"
          >
            {{ loading ? "Saving..." : actionLabel }}
          </button>
        </div>
      </div>
    </template>
  </bly-modal>
</template>

<script>
export default {
  name: "PermissionFormModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create",
    },
    entityType: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    show: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    title() {
      const entityLabels = {
        outputProfile: "output profile",
        inputProfile: "input profile",
      };
      const normalizedEntity = entityLabels[this.entityType] || this.entityType;
      return `${this.mode === "edit" ? "Edit" : "Create"} ${normalizedEntity}`.trim();
    },
    subtitle() {
      return this.mode === "edit"
        ? "Update the shared settings on the left and conditional configuration on the right."
        : "Fill in the shared settings on the left and the type-specific configuration on the right.";
    },
    actionLabel() {
      return this.mode === "edit" ? "Save changes" : "Create";
    },
    panelClass() {
      const isWideTranscodingOutputCreate = this.mode === "create" && this.entityType === "transcodingOutput";
      return isWideTranscodingOutputCreate
        ? "permission-modal__panel permission-modal__panel--wide"
        : "permission-modal__panel";
    },
  },
  methods: {
    close() {
      this.$emit("close");
      this.$emit("update:visible", false);
    },
  },
};
</script>

<style scoped>
:deep(.permission-modal__panel) {
  background: #13191d;
  color: #e5eefc;
  border: none;
  border-radius: 10px;
  width: 100%;
  min-height: auto !important;
  max-height: calc(100vh - 2rem) !important;
  margin: 1rem auto !important;
  padding: 18px 18px 16px !important;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.35);
}

:deep(.permission-modal__panel.permission-modal__panel--wide) {
  max-width: min(1700px, calc(100vw - 2rem)) !important;
}

:deep(.permission-modal__content) {
  color: #e5eefc;
  text-align: left;
}

:deep(.permission-modal__content .el-cascader),
:deep(.permission-modal__content .el-select) {
  width: 100%;
}

:deep(.permission-modal__content .el-input__inner) {
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  border: 1px solid #2a333d;
  border-radius: 3px;
  background: #161d24;
  color: #e5eefc;
  font-size: 13px;
  line-height: 32px;
  padding: 0 10px;
}

:deep(.permission-modal__content .el-textarea__inner) {
  min-height: 88px;
  border: 1px solid #2a333d;
  border-radius: 6px;
  background: #161d24;
  color: #e5eefc;
  font-size: 13px;
  line-height: 1.4;
  padding: 8px 10px;
}

:deep(.permission-modal__content .el-input__inner:focus),
:deep(.permission-modal__content .el-textarea__inner:focus) {
  border-color: #ffaa00;
}

:deep(.permission-modal__content .el-input__icon) {
  line-height: 24px;
}

:deep(.permission-modal__content .el-tag) {
  border: none;
  background: #1d2630;
  color: #dde6f3;
  height: 18px;
  line-height: 18px;
  font-size: 11px;
}

:deep(.permission-modal__content .el-form-item__label) {
  color: #dde6f3;
}

:deep(.permission-modal__content .el-divider) {
  background-color: #1f2831;
}

:deep(.permission-modal__content .el-divider__text) {
  background: #13191d;
  color: #ffaa00;
}

:deep(.permission-modal__content .el-collapse-item__header),
:deep(.permission-modal__content .el-collapse-item__wrap),
:deep(.permission-modal__content .el-collapse-item__content) {
  background: transparent;
  color: #dde6f3;
  border-bottom-color: #1f2831;
}

:deep(.permission-modal__content .wowza-target-config) {
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  margin-top: 6px;
}

:deep(.permission-modal__content .transcoding-config input:not([type="checkbox"]):not([type="radio"])),
:deep(.permission-modal__content .transcoding-config select),
:deep(.permission-modal__content .transcoding-config textarea) {
  height: 32px !important;
  max-height: 32px !important;
  min-height: 24px !important;
  width: 100% !important;
  border-radius: 3px !important;
  border: 1px solid #2a333d !important;
  background: rgba(0, 0, 0, 0.2) !important;
  padding: 0 10px !important;
  font-size: 12px !important;
  color: #e5eefc !important;
  transition-property: border-color, box-shadow !important;
  transition-duration: 150ms !important;
  outline: none !important;
}

:deep(.permission-modal__content .transcoding-config select),
:deep(.permission-modal__content .transcoding-config textarea) {
  border-width: 1px !important;
  border-style: solid !important;
  border-color: #2a333d !important;
  box-shadow: inset 0 0 0 1px #2a333d !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

:deep(.permission-modal__content .transcoding-config input:not([type="checkbox"]):not([type="radio"]):focus),
:deep(.permission-modal__content .transcoding-config select:focus),
:deep(.permission-modal__content .transcoding-config textarea:focus) {
  border-color: #ffaa00 !important;
  box-shadow: inset 0 0 0 1px #ffaa00, 0 0 0 2px rgba(255, 170, 0, 0.18) !important;
}

:deep(.permission-modal__content .transcoding-config input:not([type="checkbox"]):not([type="radio"]):disabled),
:deep(.permission-modal__content .transcoding-config select:disabled),
:deep(.permission-modal__content .transcoding-config textarea:disabled) {
  background: #151b21 !important;
  color: #94a3b8 !important;
}

:deep(.permission-modal__content .transcoding-config input:not([type="checkbox"]):not([type="radio"])[readonly]) {
  background: #151b21 !important;
  color: #94a3b8 !important;
}
@media (max-width: 640px) {
  :deep(.permission-modal__panel) {
    margin: 0.5rem auto !important;
    max-height: calc(100vh - 1rem) !important;
    padding: 16px 14px 14px !important;
  }
}
</style>
